import { ListView, ListViewColumnType, initListView } from '../../UI/view/list-view';
import { DataSource } from '../../UI/view/data-source';
import React from 'react';
import { useSelector } from 'react-redux';
import { ReceiverBankCheckbox } from './orders-receiver-bank-checkbox';
import { formatDateTimeNoTZ } from '../../../services/format';
import { selectLoggedInUserPreferences } from '../../../store/slices/auth-slice';
import {
  getUpcomingOrdersForCustomer,
  getRecentDeliveredDewarOrders,
  getDewarContainersOnSite,
  getGasContainersOnSite,
  getLiquidContainersOnSite,
  getCustomerPurityRequirements,
  getOrderGasRecentDeliveries,
  getOrderLiquidRecentDeliveries,
} from '../../../services/requests/orders/orders-requests';

// Current Customer tab (legacy divCurrentCustomerInfo). Each section is a
// reusable listview scoped to the form's live customer. The customer number
// (Order.CustomerId — the shared Praxair/site number) drives every request. The
// grids are embedded in the order form (edit + create), so they read the live
// value from the form `watch` (2nd requestArgs arg) and refetch via watchFields.
const customerId = (_state: any, watch?: any) => watch?.('customerId');
const orderTypeId = (_state: any, watch?: any) => watch?.('orderTypeId');

// Legacy shows the raw stored datetime (MM/dd/yyyy HH:mm) for the on-site
// Date Shipped / Est. Return Date columns. The shared `datetime` column type
// only renders the date, so these columns use a cell that keeps the time.
const DateTimeCell = ({ value }: any) => {
  const { datePreference } = (useSelector(selectLoggedInUserPreferences) as any) ?? {};
  return value ? formatDateTimeNoTZ(value, datePreference) : '';
};
const dateTimeCell = ({ value }: any) => React.createElement(DateTimeCell, { value });

// --- Gas / Liquid: Recent Deliveries (legacy gvRecentDeliveries) -------------
// Both order types share the same 3-column layout: Date Shipped / Order # /
// Volume Delivered (legacy ListGas|LiquidBillingRecentDeliveries, top 10).
const recentDeliveriesColumns = [
  { Header: 'Date Shipped', accessor: 'dateShipped', type: ListViewColumnType.date, defaultSort: true, defaultSortDirection: 'desc' },
  {
    Header: 'Order #',
    accessor: 'orderNumber',
    type: ListViewColumnType.link,
    linkPath: ({ row }: any) => `/orders/edit/${row?.original?.orderId}`,
  },
  { Header: 'Volume Delivered', accessor: 'volumeDelivered' },
];

export const gasRecentDeliveriesDataSource = {
  name: 'orderGasRecentDeliveries',
  pk: 'orderNumber',
  request: getOrderGasRecentDeliveries,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

export const gasRecentDeliveriesListView = {
  hideGlobalFilter: true,
  columns: recentDeliveriesColumns,
} as ListView;

export const GasRecentDeliveriesListView = initListView(gasRecentDeliveriesListView, gasRecentDeliveriesDataSource);

export const liquidRecentDeliveriesDataSource = {
  name: 'orderLiquidRecentDeliveries',
  pk: 'orderNumber',
  request: getOrderLiquidRecentDeliveries,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

export const liquidRecentDeliveriesListView = {
  hideGlobalFilter: true,
  columns: recentDeliveriesColumns,
} as ListView;

export const LiquidRecentDeliveriesListView = initListView(liquidRecentDeliveriesListView, liquidRecentDeliveriesDataSource);

// --- Gas / Liquid: Containers On Site ---------------------------------------
export const gasContainersOnSiteDataSource = {
  name: 'orderGasContainersOnSite',
  pk: 'containerId',
  request: getGasContainersOnSite,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

const containersOnSiteColumns = [
  { Header: 'Container ID', accessor: 'containerId' },
  { Header: 'Date Shipped', accessor: 'shippingCompleteDate', defaultSort: true, defaultSortDirection: 'desc', Cell: dateTimeCell },
  { Header: 'Est. Return Date', accessor: 'estimatedReturnDate', Cell: dateTimeCell },
  {
    Header: 'Order #',
    accessor: 'orderNumber',
    type: ListViewColumnType.link,
    linkPath: ({ row }: any) => `/orders/edit/${row?.original?.orderId}`,
  },
];

// Gas shows the editable "Receiver Bank" checkbox (legacy chkReceiverBank →
// GasTrackingNumbers.IsReceiverBank). Gas-only — the flag exists only on
// GasTrackingNumbers, so Liquid keeps the base columns.
const receiverBankColumn = {
  Header: 'Receiver Bank',
  id: 'receiverBank',
  accessor: 'isReceiverBank',
  disableSortBy: true,
  Cell: ({ row }: any) => React.createElement(ReceiverBankCheckbox, { row: row?.original }),
};

const gasContainersOnSiteColumns = [...containersOnSiteColumns, receiverBankColumn];

export const gasContainersOnSiteListView = {
  hideGlobalFilter: true,
  columns: gasContainersOnSiteColumns,
} as ListView;

export const GasContainersOnSiteListView = initListView(gasContainersOnSiteListView, gasContainersOnSiteDataSource);

export const liquidContainersOnSiteDataSource = {
  name: 'orderLiquidContainersOnSite',
  pk: 'containerId',
  request: getLiquidContainersOnSite,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

export const liquidContainersOnSiteListView = {
  hideGlobalFilter: true,
  columns: containersOnSiteColumns,
} as ListView;

export const LiquidContainersOnSiteListView = initListView(liquidContainersOnSiteListView, liquidContainersOnSiteDataSource);

// --- Gas / Liquid: Customer Purity Requirements -----------------------------
export const customerPurityDataSource = {
  name: 'orderCustomerPurity',
  pk: 'component',
  request: getCustomerPurityRequirements,
  requestArgs: (state: any, watch?: any) => [orderTypeId(state, watch), customerId(state, watch)],
  watchFields: ['orderTypeId', 'customerId'],
} as DataSource;

export const customerPurityListView = {
  hideGlobalFilter: true,
  columns: [
    { Header: 'Requirement', accessor: 'component' },
    { Header: 'Max', accessor: 'max' },
    { Header: 'Units', accessor: 'units' },
    {
      Header: 'Included',
      accessor: 'included',
      Cell: ({ value }: any) =>
        React.createElement('input', { type: 'checkbox', checked: !!value, readOnly: true, disabled: true }),
    },
  ],
} as ListView;

export const CustomerPurityListView = initListView(customerPurityListView, customerPurityDataSource);

// --- Dewar: Recent Dewar Deliveries -----------------------------------------
export const recentDewarDeliveriesDataSource = {
  name: 'orderRecentDewarDeliveries',
  pk: 'id',
  request: getRecentDeliveredDewarOrders,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

export const recentDewarDeliveriesListView = {
  hideGlobalFilter: true,
  columns: [
    { Header: 'Date Delivered', accessor: 'dateFulfilled', defaultSort: true, Cell: dateTimeCell },
    {
      Header: 'Order #',
      accessor: 'orderNumber',
      type: ListViewColumnType.link,
      linkPath: ({ row }: any) => `/orders/edit/${row?.original?.id}`,
    },
    { Header: 'Manifest', accessor: 'dewarManifestLine' },
  ],
} as ListView;

export const RecentDewarDeliveriesListView = initListView(recentDewarDeliveriesListView, recentDewarDeliveriesDataSource);

// --- Dewar: Dewar Containers On Site ----------------------------------------
export const dewarContainersOnSiteDataSource = {
  name: 'orderDewarContainersOnSite',
  pk: 'serialNumber',
  request: getDewarContainersOnSite,
  requestArgs: customerId,
  watchFields: ['customerId'],
} as DataSource;

export const dewarContainersOnSiteListView = {
  hideGlobalFilter: true,
  columns: [
    { Header: 'Serial Number', accessor: 'serialNumber' },
    { Header: 'Product', accessor: 'productType' },
    { Header: 'Date Delivered', accessor: 'dateDelivered', defaultSort: true, Cell: dateTimeCell },
    {
      Header: 'Order #',
      accessor: 'orderNumber',
      type: ListViewColumnType.link,
      linkPath: ({ row }: any) => `/orders/edit/${row?.original?.orderId}`,
    },
    { Header: 'Days Out', accessor: 'daysOut' },
  ],
} as ListView;

export const DewarContainersOnSiteListView = initListView(dewarContainersOnSiteListView, dewarContainersOnSiteDataSource);

// --- Dewar: Upcoming Orders (by customer) -----------------------------------
export const upcomingOrdersForCustomerDataSource = {
  name: 'orderUpcomingForCustomer',
  pk: 'id',
  request: getUpcomingOrdersForCustomer,
  requestArgs: (state: any, watch?: any) => [orderTypeId(state, watch), customerId(state, watch)],
  watchFields: ['orderTypeId', 'customerId'],
} as DataSource;

export const upcomingOrdersForCustomerListView = {
  hideGlobalFilter: true,
  columns: [
    { Header: 'Customer', accessor: 'customerName' },
    { Header: 'Date Requested', accessor: 'dateRequested', type: ListViewColumnType.date, defaultSort: true },
    {
      Header: 'Order #',
      accessor: 'orderNumber',
      type: ListViewColumnType.link,
      linkPath: ({ row }: any) => `/orders/edit/${row?.original?.id}`,
    },
  ],
} as ListView;

export const UpcomingOrdersForCustomerListView = initListView(upcomingOrdersForCustomerListView, upcomingOrdersForCustomerDataSource);
